import type { BusinessOperation, BusinessWorkflow } from '@/services/instanceWorkflows'

export type WorkflowEdit = { kind: 'metadata'; title: string; description: string; entryStepId?: string }
  | { kind: 'add' | 'update'; id: string; title: string; operationId: string }
  | { kind: 'remove'; id: string }
  | { kind: 'connect' | 'disconnect'; source: string; target: string }
  | { kind: 'positions'; positions: Record<string, { x: number; y: number }> }

/** Business transitions span turns. They are never native WorkPlan dependency edges. */
export const editWorkflow = (flow: BusinessWorkflow, command: WorkflowEdit, operations: BusinessOperation[]): BusinessWorkflow => {
  const operationsById = new Map(operations.map((item) => [item.operation_id, item]))
  const handlers: Record<WorkflowEdit['kind'], () => BusinessWorkflow> = {
    metadata: () => command.kind === 'metadata' ? { ...flow, title: command.title.trim(), description: command.description, entry_step_id: command.entryStepId ?? flow.entry_step_id } : flow,
    add: () => {
      if (command.kind !== 'add') return flow
      if (flow.steps.some((item) => item.step_id === command.id)) throw new Error('步骤标识重复')
      if (!operationsById.has(command.operationId)) throw new Error('此业务操作当前不可用')
      return { ...flow, steps: [...flow.steps, { step_id: command.id, title: command.title.trim(), operation_id: command.operationId,
        position: { x: 80 + (flow.steps.length % 2) * 340, y: 60 + Math.floor(flow.steps.length / 2) * 180 } }] }
    },
    update: () => {
      if (command.kind !== 'update') return flow
      if (!operationsById.has(command.operationId)) throw new Error('此业务操作当前不可用')
      if (!flow.steps.some((item) => item.step_id === command.id)) throw new Error('步骤已不存在')
      const event = operationsById.get(command.operationId)?.action_key ?? 'continue'
      return { ...flow, steps: flow.steps.map((item) => item.step_id === command.id ? { ...item, title: command.title.trim(), operation_id: command.operationId } : item),
        transitions: flow.transitions.map((item) => item.target === command.id ? { ...item, event, label: command.title.trim() } : item) }
    },
    remove: () => command.kind === 'remove' ? { ...flow, entry_step_id: flow.entry_step_id === command.id ? null : flow.entry_step_id, steps: flow.steps.filter((item) => item.step_id !== command.id),
      transitions: flow.transitions.filter((item) => item.source !== command.id && item.target !== command.id) } : flow,
    connect: () => {
      if (command.kind !== 'connect') return flow
      const target = flow.steps.find((item) => item.step_id === command.target)
      const operation = operationsById.get(target?.operation_id ?? '')
      if (!flow.steps.some((item) => item.step_id === command.source) || !target || !operation) throw new Error('连线引用了不可用的步骤')
      const event = operation.action_key ?? 'continue'
      if (flow.transitions.some((item) => item.source === command.source && item.event === event)) throw new Error('同一业务事件已有去向，请先删除原连线')
      return { ...flow, transitions: [...flow.transitions, { source: command.source, target: command.target, event, label: operation.label }] }
    },
    disconnect: () => command.kind === 'disconnect' ? { ...flow,
      transitions: flow.transitions.filter((item) => item.source !== command.source || item.target !== command.target) } : flow,
    positions: () => command.kind === 'positions' ? { ...flow,
      steps: flow.steps.map((item) => ({ ...item, position: command.positions[item.step_id] ?? item.position })) } : flow,
  }
  const next = handlers[command.kind]()
  if (!next.title || next.steps.some((item) => !item.title)) throw new Error('请输入名称')
  return next
}
